import React, { createContext, useContext, useState, useCallback } from 'react';

interface DialogState {
  isOpen: boolean;
  formData: Record<string, any>;
  editingItem: any | null;
}

type FormUpdater =
  | Record<string, any>
  | ((prev: Record<string, any>) => Record<string, any>);

interface DialogPersistenceContextType {
  getDialogState: (pageId: string) => DialogState | undefined;
  setDialogState: (pageId: string, state: Partial<DialogState>) => void;
  updateFormData: (pageId: string, updater: FormUpdater) => void;
  clearDialogState: (pageId: string) => void;
}

const DialogPersistenceContext = createContext<DialogPersistenceContextType | undefined>(undefined);

export function DialogPersistenceProvider({ children }: { children: React.ReactNode }) {
  const [dialogStates, setDialogStates] = useState<Record<string, DialogState>>({});

  const getDialogState = useCallback((pageId: string) => {
    return dialogStates[pageId];
  }, [dialogStates]);

  const setDialogState = useCallback((pageId: string, state: Partial<DialogState>) => {
    setDialogStates(prev => ({
      ...prev,
      [pageId]: {
        ...prev[pageId],
        ...state,
      } as DialogState
    }));
  }, []);

  const updateFormData = useCallback((pageId: string, updater: FormUpdater) => {
    setDialogStates(prev => {
      const current = prev[pageId];
      const currentForm = current?.formData ?? {};
      const nextForm =
        typeof updater === 'function' ? updater(currentForm) : { ...currentForm, ...updater };
      return {
        ...prev,
        [pageId]: {
          ...current,
          formData: nextForm,
        } as DialogState,
      };
    });
  }, []);

  const clearDialogState = useCallback((pageId: string) => {
    setDialogStates(prev => {
      const newState = { ...prev };
      delete newState[pageId];
      return newState;
    });
  }, []);

  return (
    <DialogPersistenceContext.Provider value={{ getDialogState, setDialogState, updateFormData, clearDialogState }}>
      {children}
    </DialogPersistenceContext.Provider>
  );
}

export function useDialogPersistence(pageId: string) {
  const context = useContext(DialogPersistenceContext);
  
  if (context === undefined) {
    throw new Error('useDialogPersistence must be used within a DialogPersistenceProvider');
  }

  const dialogState = context.getDialogState(pageId);

  const setOpen = useCallback((isOpen: boolean) => {
    context.setDialogState(pageId, { isOpen });
  }, [context, pageId]);

  // Accepts both an object (merged onto current formData) and a functional
  // updater (prev => next). Using the context-level setter guarantees we
  // always merge against the latest persisted state, never a stale closure.
  const setFormData = useCallback(
    (formData: Record<string, any> | ((prev: Record<string, any>) => Record<string, any>)) => {
      context.updateFormData(pageId, formData);
    },
    [context, pageId],
  );

  const setEditingItem = useCallback((editingItem: any | null) => {
    context.setDialogState(pageId, { editingItem });
  }, [context, pageId]);

  const closeAndClear = useCallback(() => {
    context.clearDialogState(pageId);
  }, [context, pageId]);

  return {
    isOpen: dialogState?.isOpen ?? false,
    formData: dialogState?.formData ?? {},
    editingItem: dialogState?.editingItem ?? null,
    setOpen,
    setFormData,
    setEditingItem,
    closeAndClear,
  };
}
